import { usePathname, useRouter, useSearchParams } from '@/lib/react-router-compat';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ArrowLeft, Pencil } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import Link from '@/components/core/router-link';
import { BREADCRUMB_CUSTOM_TITLES } from '@/constant/breadcrumb-custom-title';
import PageBreadcrumb from '@/components/breadcrumb/breadcrumb';
import {
  useGetToolDetails,
  useToolAssociatedAgentCount,
} from '@/modules/gpt-chats/hooks/use-tools';
import { EditToolModal } from '@/modules/gpt-chats/components/tools/edit-tool-modal';
import { useState } from 'react';

import { Sparkles as CircleSpark } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const ToolDetailsLayout = ({
  children,
  toolId,
}: {
  children: React.ReactNode;
  toolId: string;
}) => {
  const pathname = usePathname();
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const copiedBlocksTool = searchParams.get('copiedBlocksTool') === 'true';
  const { data: toolDetails, isLoading } = useGetToolDetails(toolId, isBlocksTool);
  const { data: associatedAgentCount } = useToolAssociatedAgentCount(toolId, !isBlocksTool);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const getCurrentTab = () => {
    if (pathname.includes('/authentication')) return 'authentication';
    if (pathname.includes('/api')) return 'api';
    if (pathname.includes('/tutorial-guide')) return 'tutorial-guide';
    if (pathname.includes('/dashboard')) return 'dashboard';
    return 'authentication';
  };

  const handleTabChange = (value: string) => {
    router.push(
      `/ai/tool-details/${toolId}/${value}?isBlocksTool=${isBlocksTool}&copiedBlocksTool=${copiedBlocksTool}`
    );
  };

  const toolName = toolDetails?.Name || t('TOOL_TAB');

  BREADCRUMB_CUSTOM_TITLES[`/ai/tool-details/${toolId}`] = 'AI Tools';

  return (
    <div className="flex flex-col gap-6">
      <PageBreadcrumb breadcrumbIndex={3} />

      <div className="flex items-center gap-4">
        <Link
          href={`/ai/tools${isBlocksTool && !copiedBlocksTool ? '?toolType=blocks-tools' : ''}`}
        >
          <Button variant="ghost" size="icon" className="-ml-3 hover:bg-transparent">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{isLoading ? t('LOADING') : toolName}</h1>
          <Button
            variant="ghost"
            size="icon"
            className="text-primary"
            onClick={() => setIsEditModalOpen(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex w-full justify-between">
        <Tabs value={getCurrentTab()} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="authentication">{t('AUTHENTICATION')}</TabsTrigger>
            <TabsTrigger value="api">{t('API')}</TabsTrigger>
            <TabsTrigger value="tutorial-guide">{t('TUTORIAL_GUIDE')}</TabsTrigger>
            {!isBlocksTool && <TabsTrigger value="dashboard">{t('DASHBOARD')}</TabsTrigger>}
          </TabsList>
        </Tabs>
        {!isBlocksTool && (
          <div className="m-2 hidden flex-row items-center gap-2 rounded-sm bg-border px-2 py-1.5 lg:flex">
            <CircleSpark className="aspect-square h-3 w-3 p-0 text-cyan-700" />
            <span className="p-0 text-sm text-medium-emphasis">{t('ASSOCIATED_AGENTS')} </span>
            <span className="p-0 text-sm text-high-emphasis">
              {associatedAgentCount?.data ?? 0}
            </span>
          </div>
        )}
      </div>

      <div className="mb-2">{children}</div>

      <EditToolModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        copiedBlocksTool={copiedBlocksTool}
        tool={
          toolDetails
            ? {
                id: toolDetails._id,
                name: toolDetails.Name,
                url: toolDetails.APIConfig?.BaseURL || '',
                description: toolDetails.Description,
                status: toolDetails.Status,
              }
            : undefined
        }
        isBlocksTool={isBlocksTool}
      />
    </div>
  );
};
